import React, { useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet-async";
import { UNIVERSITIES, COUNTRIES } from "../data/index";
import "./UniversityPage.css";

gsap.registerPlugin(ScrollTrigger);

export default function UniversityPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const rootRef = useRef(null);

  const uni = UNIVERSITIES[slug];
  const country = uni ? COUNTRIES[uni.country] : null;

  useEffect(() => {
    if (!uni) { navigate("/"); return; }
  }, [uni, navigate]);

  useEffect(() => {
    if (!uni) return;
    const ctx = gsap.context(() => {
      // Hero
      gsap.fromTo(".up-hero-badge", { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.55, delay: 0.1, ease: "power3.out" });
      gsap.fromTo(".up-hero-h1 .line-inner", { y: "110%" }, { y: 0, duration: 0.95, delay: 0.2, stagger: 0.1, ease: "power4.out" });
      gsap.fromTo(".up-hero-meta", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, delay: 0.5, ease: "power3.out" });
      gsap.fromTo(".up-hero-actions", { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.65, delay: 0.65, ease: "power3.out" });
      gsap.fromTo(".up-hero-img-wrap", { opacity: 0, scale: 1.04 }, { opacity: 1, scale: 1, duration: 1.2, delay: 0.05, ease: "power3.out" });

      // Scroll reveals
      const els = [".up-overview", ".up-courses", ".up-placement", ".up-requirements", ".up-cta"];
      els.forEach((sel) => {
        gsap.fromTo(sel,
          { opacity: 0, y: 36 },
          {
            opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
            scrollTrigger: { trigger: sel, start: "top 83%", once: true }
          }
        );
      });

      gsap.fromTo(".up-course-row",
        { opacity: 0, x: -20 },
        {
          opacity: 1, x: 0, duration: 0.55, stagger: 0.07, ease: "power3.out",
          scrollTrigger: { trigger: ".up-courses", start: "top 78%", once: true }
        }
      );

      gsap.fromTo(".up-employer-tag",
        { opacity: 0, scale: 0.88 },
        {
          opacity: 1, scale: 1, duration: 0.45, stagger: 0.06, ease: "back.out(1.3)",
          scrollTrigger: { trigger: ".up-placement", start: "top 80%", once: true }
        }
      );
    }, rootRef);

    ScrollTrigger.refresh();
    return () => ctx.revert();
  }, [uni]);

  if (!uni || !country) return null;

  return (
    <div className="up" ref={rootRef}>
      <Helmet>
        <title>{uni.name} | Courses, Fees & Admissions | ISTP Overseas</title>
        <meta name="description" content={`Explore ${uni.name} — ${uni.qsRank}, located in ${uni.city}. Tuition: ${uni.tuition.international}, IELTS: ${uni.ielts}. ISTP Overseas has placed ${uni.istpStudents}+ students here.`} />
        <link rel="canonical" href={`https://www.istpoverseas.com/university/${slug}`} />
        <meta property="og:title" content={`${uni.name} | ISTP Overseas`} />
        <meta property="og:description" content={`${uni.qsRank} university in ${uni.city}. Admissions guidance from ISTP Overseas.`} />
        <meta property="og:url" content={`https://www.istpoverseas.com/university/${slug}`} />
      </Helmet>
      {/* ── Breadcrumb ── */}
      <div className="up-breadcrumb">
        <div className="up-bc-inner">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to={`/study-in-${uni.country}`}>Study in {country.name}</Link>
          <span>/</span>
          <span>{uni.shortName}</span>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="up-hero">
        <div className="up-hero-img-wrap">
          <img src={uni.heroImg} alt={uni.name} />
          <div className="up-hero-overlay" />
        </div>

        <div className="up-hero-content">
          <div className="up-hero-inner">
            <div className="up-hero-badge-row">
              <span className="up-hero-badge">
                {uni.flag} {uni.city}
              </span>
              <span className="up-rank-badge">{uni.qsRank}</span>
            </div>

            <h1 className="up-hero-h1">
              <span className="line-mask"><span className="line-inner">{uni.name}</span></span>
            </h1>

            <div className="up-hero-meta">
              <div className="up-meta-item">
                <span className="up-meta-label">Founded</span>
                <span className="up-meta-value">{uni.founded}</span>
              </div>
              <div className="up-meta-sep" />
              <div className="up-meta-item">
                <span className="up-meta-label">IELTS Req.</span>
                <span className="up-meta-value">{uni.ielts}</span>
              </div>
              <div className="up-meta-sep" />
              <div className="up-meta-item">
                <span className="up-meta-label">Main Intake</span>
                <span className="up-meta-value">{uni.intakes[0]}</span>
              </div>
              <div className="up-meta-sep" />
              <div className="up-meta-item">
                <span className="up-meta-label">ISTP Alumni</span>
                <span className="up-meta-value">{uni.istpStudents}+ students</span>
              </div>
            </div>

            <div className="up-hero-actions">
              <Link to="/book-slot" className="up-btn-primary">Apply via ISTP</Link>
              <Link to={`/study-in-${uni.country}`} className="up-btn-ghost">
                More {country.flag} {country.name} Universities →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick facts ── */}
      <section className="up-facts-bar">
        <div className="up-container">
          <div className="up-facts-inner">
            <div className="up-fact">
              <span className="up-fact-label">Int'l Tuition</span>
              <span className="up-fact-value">{uni.tuition.international}</span>
            </div>
            <div className="up-fact">
              <span className="up-fact-label">Living Costs</span>
              <span className="up-fact-value">{uni.living}</span>
            </div>
            <div className="up-fact">
              <span className="up-fact-label">Placement Rate</span>
              <span className="up-fact-value">{uni.placementStats.rate}</span>
            </div>
            <div className="up-fact">
              <span className="up-fact-label">Avg. Grad Salary</span>
              <span className="up-fact-value">{uni.placementStats.avgSalary}</span>
            </div>
            <div className="up-fact">
              <span className="up-fact-label">GRE Requirement</span>
              <span className="up-fact-value">{uni.gre}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Overview ── */}
      <section className="up-overview">
        <div className="up-container">
          <div className="up-overview-inner">
            <div className="up-overview-text">
              <span className="up-eyebrow">About the University</span>
              <h2 className="up-section-h2">Overview</h2>
              <p className="up-overview-para">{uni.overview}</p>
              <div className="up-overview-tags">
                {uni.intakes.map((intake, i) => (
                  <span key={i} className="up-tag up-tag--navy">🗓 {intake}</span>
                ))}
                <span className="up-tag up-tag--red">📊 {uni.qsRank}</span>
                <span className="up-tag up-tag--navy">🎓 Est. {uni.founded}</span>
              </div>
            </div>
            <div className="up-overview-aside">
              <div className="up-aside-card">
                <h3 className="up-aside-title">ISTP Success at {uni.shortName}</h3>
                <div className="up-istp-num">{uni.istpStudents}+</div>
                <p className="up-aside-sub">Students placed by ISTP at this university</p>
                <Link to="/book-slot" className="up-aside-cta">Get Your Admission →</Link>
              </div>
              <div className="up-aside-card up-aside-card--light">
                <h3 className="up-aside-title">Tuition Fees</h3>
                <div className="up-fee-rows">
                  <div className="up-fee-row">
                    <span>International Students</span>
                    <strong>{uni.tuition.international}</strong>
                  </div>
                  <div className="up-fee-row">
                    <span>Domestic Students</span>
                    <strong>{uni.tuition.domestic}</strong>
                  </div>
                  <div className="up-fee-row">
                    <span>Living Expenses</span>
                    <strong>{uni.living}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Top Courses ── */}
      <section className="up-courses">
        <div className="up-container">
          <span className="up-eyebrow">Academics</span>
          <h2 className="up-section-h2">Top Courses at {uni.shortName}</h2>
          <div className="up-courses-table">
            <div className="up-course-header">
              <span>Programme</span>
              <span>Duration</span>
              <span>Language</span>
              <span></span>
            </div>
            {uni.topCourses.map((course, i) => (
              <div key={i} className="up-course-row">
                <span className="up-course-name">{course.name}</span>
                <span className="up-course-duration">{course.duration}</span>
                <span className="up-course-lang">{course.language}</span>
                <Link to="/book-slot" className="up-course-apply">Apply →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Placement ── */}
      <section className="up-placement">
        <div className="up-container">
          <div className="up-placement-inner">
            <div className="up-placement-stats">
              <span className="up-eyebrow">Career Outcomes</span>
              <h2 className="up-section-h2">Placements &amp; Careers</h2>
              <div className="up-pl-stat-grid">
                <div className="up-pl-stat">
                  <span className="up-pl-num">{uni.placementStats.rate}</span>
                  <span className="up-pl-label">Graduate Employment Rate</span>
                </div>
                <div className="up-pl-stat">
                  <span className="up-pl-num">{uni.placementStats.avgSalary}</span>
                  <span className="up-pl-label">Average Starting Salary</span>
                </div>
              </div>
            </div>
            <div className="up-employers">
              <h3 className="up-employers-title">Top Employers</h3>
              <div className="up-employer-tags">
                {uni.placementStats.topEmployers.map((e, i) => (
                  <span key={i} className="up-employer-tag">{e}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Requirements ── */}
      <section className="up-requirements">
        <div className="up-container">
          <span className="up-eyebrow">Admission</span>
          <h2 className="up-section-h2">Entry Requirements</h2>
          <div className="up-req-grid">
            <div className="up-req-card">
              <span className="up-req-icon">📝</span>
              <h3>English Proficiency</h3>
              <p>IELTS minimum score: <strong>{uni.ielts}</strong></p>
              <p>TOEFL accepted as an alternative — check specific programme requirements.</p>
            </div>
            <div className="up-req-card">
              <span className="up-req-icon">🎓</span>
              <h3>Academic Qualifications</h3>
              <p>Recognised bachelor's degree (or equivalent) in a relevant field. Typically 60%+ or equivalent GPA.</p>
            </div>
            <div className="up-req-card">
              <span className="up-req-icon">📊</span>
              <h3>GRE / GMAT</h3>
              <p>{uni.gre}. ISTP advises on whether your target programme requires it and helps with preparation strategy.</p>
            </div>
            <div className="up-req-card">
              <span className="up-req-icon">📄</span>
              <h3>Documents Required</h3>
              <p>Statement of Purpose, 2 Letters of Recommendation, CV/Resume, Academic Transcripts, Passport copy.</p>
            </div>
            <div className="up-req-card">
              <span className="up-req-icon">🗓</span>
              <h3>Intake Deadlines</h3>
              {uni.intakes.map((intake, i) => (
                <p key={i}>{intake} — apply 4–6 months in advance for best chances.</p>
              ))}
            </div>
            <div className="up-req-card up-req-card--cta">
              <span className="up-req-icon">🤝</span>
              <h3>ISTP Can Help</h3>
              <p>Our counsellors manage your entire application — SOP, LORs, transcripts, and follow-ups.</p>
              <Link to="/book-slot" className="up-btn-primary up-btn-primary--sm">Book Free Evaluation</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="up-cta">
        <div className="up-container">
          <div className="up-cta-inner">
            <div className="up-cta-text">
              <h2 className="up-cta-h2">
                Ready to apply to<br /><em>{uni.shortName}?</em>
              </h2>
              <p className="up-cta-sub">
                ISTP has placed {uni.istpStudents}+ students here. Our expert counsellors know exactly what this university looks for — and how to make your application stand out.
              </p>
              <div className="up-cta-actions">
                <Link to="/book-slot" className="up-btn-primary">Start Your Application</Link>
                <a href="https://wa.me/919110714332" target="_blank" rel="noopener noreferrer" className="up-btn-wa">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.878-1.42C8.34 21.47 10.13 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                  </svg>
                  Ask on WhatsApp
                </a>
              </div>
            </div>
            <div className="up-cta-card">
              <div className="up-cta-uni-badge">
                <span className="up-cta-flag">{uni.flag}</span>
                <div>
                  <strong>{uni.shortName}</strong>
                  <span>{uni.city}</span>
                </div>
              </div>
              <div className="up-cta-stats">
                <div><span>{uni.qsRank}</span><em>Global Ranking</em></div>
                <div><span>{uni.placementStats.rate}</span><em>Placement Rate</em></div>
                <div><span>{uni.istpStudents}+</span><em>ISTP Alumni</em></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
